export function WhistleblowerPolicy() {


    return (
        <div className="font-mono  mx-auto px-4 py-10 leading-relaxed text-sm">
            <h1 className="text-2xl font-bold mb-6">Whistleblower Policy</h1>

            <h2 className="text-xl font-bold mt-8 mb-3">1. Purpose and Scope</h2>
            <p className="mb-3">
                Netstratum Technologies is committed to conducting its business with integrity, transparency, and in accordance with applicable laws and ethical standards. This Whistleblower Policy provides a safe and confidential channel for employees, contractors, suppliers, and other stakeholders to raise genuine concerns about actual or suspected misconduct, illegal activity, or unethical behaviour within Netstratum Technologies or its supply chain.
            </p>
            <p className="mb-2">This policy applies to all individuals associated with Netstratum Technologies, including -</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Current and former employees (full-time, part-time, and contract staff)</li>
                <li>Interns and trainees</li>
                <li>Consultants, subcontractors, and agency workers</li>
                <li>Suppliers and business partners</li>
                <li>Members of the Board of Directors</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-3">2. Our Commitment</h2>
            <p className="mb-2">Netstratum Technologies is committed to -</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Providing a safe and accessible mechanism for reporting concerns in good faith.</li>
                <li>Treating all reports seriously and investigating them in a fair, timely, and confidential manner.</li>
                <li>Protecting individuals who raise concerns in good faith from retaliation, victimisation, or any form of detriment.</li>
                <li>Maintaining the confidentiality of reporters to the fullest extent possible, consistent with the requirements of any investigation.</li>
            </ul>
            <p className="mb-4">
                We understand that raising a concern can be difficult. This policy is designed to make the process as straightforward and safe as possible.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">3. What Concerns Can Be Reported?</h2>
            <p className="mb-4">
                This policy covers a wide range of serious concerns. The table below provides examples of the types of issues that can be reported -
            </p>

            <table className="w-full border border-gray-300 border-collapse mb-6 text-sm">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 p-2 text-left font-semibold">Category</th>
                        <th className="border border-gray-300 p-2 text-left font-semibold">Description</th>
                    </tr>
                </thead>
                <tbody className="[&_tr:nth-child(even)]:bg-gray-50">
                    <tr>
                        <td className="border border-gray-300 p-2">Bribery and Corruption</td>
                        <td className="border border-gray-300 p-2">
                            Offering, giving, receiving, or soliciting anything of value to improperly influence a business or official decision.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Fraud and Financial Misconduct</td>
                        <td className="border border-gray-300 p-2">
                            Falsification of records, expense fraud, embezzlement, or any dishonest financial practice.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Violations of Law or Regulation</td>
                        <td className="border border-gray-300 p-2">
                            Breaches of applicable legislation, regulatory requirements, or industry standards.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Workplace Harassment and Discrimination</td>
                        <td className="border border-gray-300 p-2">
                            Bullying, intimidation, sexual harassment, or any discriminatory treatment of employees or others.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Health, Safety, and Environmental Risks</td>
                        <td className="border border-gray-300 p-2">
                            Unsafe working conditions, environmental violations, or conduct that puts individuals at risk of harm.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Conflicts of Interest</td>
                        <td className="border border-gray-300 p-2">
                            Undisclosed relationships or activities that may improperly influence decisions in the organisation.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Modern Slavery</td>
                        <td className="border border-gray-300 p-2">
                            Any form of forced labour, human trafficking, or exploitation within our operations or supply chain.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Data Privacy Breaches</td>
                        <td className="border border-gray-300 p-2">
                            Unauthorised access to, or disclosure of, personal or confidential information.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Policy and Procedure Violations</td>
                        <td className="border border-gray-300 p-2">
                            Significant breaches of company policies, including the Code of Conduct.
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-gray-300 p-2">Other Serious Misconduct</td>
                        <td className="border border-gray-300 p-2">
                            Any other conduct that poses a genuine risk to the integrity, safety, or reputation of Netstratum Technologies or its stakeholders.
                        </td>
                    </tr>
                </tbody>
            </table>

            <p className="mb-4">
                This list is not exhaustive. If you are unsure whether a concern falls within the scope of this policy, we encourage you to report it. Concerns that do not fall under this policy may be directed through the company's Grievance Procedure, as appropriate.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">4. How to Report a Concern</h2>

            <h3 className="text-lg font-bold mt-6 mb-2">4.1 Internal Reporting Channels</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Directly with your Reporting Manager, if you feel comfortable doing so.</li>
                <li>With the Human Resources Manager.</li>
                <li>With any member of senior management above your Reporting Manager.</li>
            </ul>

            <h3 className="text-lg font-bold mt-6 mb-2">4.2 Formal Written Complaint</h3>
            <p className="mb-2">Your written report should include, where possible:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>A clear description of the concern or incident(s).</li>
                <li>The date(s) and location(s) of the incident(s).</li>
                <li>The names of individuals involved or witnesses.</li>
                <li>Supporting documentation or evidence.</li>
                <li>Your contact details (unless anonymous).</li>
            </ul>

            <h3 className="text-lg font-bold mt-6 mb-2">4.3 Sexual Harassment</h3>
            <p className="mb-4">
                In cases involving sexual harassment, the aggrieved individual may report directly to the Internal Complaints Committee (ICC), in accordance with applicable laws.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">5. Anonymity and Confidentiality</h2>
            <p className="mb-4">
                You may submit a report anonymously. Anonymous reports will be investigated to the extent possible. Where you identify yourself, your identity will be treated as confidential and disclosed only on a need-to-know basis or as required by law.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">6. Protection from Retaliation</h2>
            <p className="mb-2">Netstratum Technologies strictly prohibits retaliation against whistleblowers.</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Dismissal, demotion, or suspension</li>
                <li>Unfair disciplinary action</li>
                <li>Harassment or victimisation</li>
                <li>Denial of opportunities</li>
            </ul>
            <p className="mb-4">
                Any retaliation will result in disciplinary action, including termination. Report retaliation immediately to HR or management.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">7. Investigation Process</h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Acknowledgement of report</li>
                <li>Initial assessment</li>
                <li>Formal investigation</li>
                <li>Outcome and action</li>
                <li>Communication of outcome (where possible)</li>
            </ul>
            <p className="mb-4">
                Confidentiality will be maintained, and individuals implicated will have an opportunity to respond.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">8. Good Faith Reporting</h2>
            <p className="mb-4">
                Reports must be made honestly and in good faith. False or malicious reports may result in disciplinary action, but genuine concerns remain protected.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-3">9. Governance and Review</h2>
            <p className="mb-4">
                This policy is owned by the HR Department and reviewed annually. Netstratum reserves the right to amend this policy at any time.
            </p>

        </div>
    )
}